<script setup lang="ts">
import {
  ref,
  toRef,
  shallowRef,
  onMounted,
  defineAsyncComponent,
  computed,
} from 'vue';
import { useRsvpStore } from '@/stores/rsvp/rsvp.store';
import { PageFooter } from '@/components/clients/common';
import LangChooser from '@/components/common/LangChooser.vue';
import formVersionList from '@/components/clients/rsvp/components/form/versions';
import ClockWidget from '../components/ClockWidgetV2.vue';
import { useStateStore } from '@/stores/state.store';
import HeaderWidget from '../../common/HeaderWidget.vue';
import { invertColor } from '@/helpers/color';
import { getStringDate } from '@/helpers/date';

import { rings, pin, church } from '../../../../assets/custom/index.js';

const stateStore = useStateStore();
const rsvpStore = useRsvpStore();

const places = toRef(() => rsvpStore.getPlaces());

const textFont =
  (rsvpStore.clientData.common.textFont || '').trim() !== ''
    ? rsvpStore.getCommonData.textFont
    : 'Lexend Deca';

const headerTextFont =
  (rsvpStore.clientData.common.headerTextFont || '').trim() !== ''
    ? rsvpStore.clientData.common.headerTextFont
    : 'Parisienne';

const mainColor = toRef(() => rsvpStore.getColors.mainColor);
const inversedMainColor = computed(() => {
  return invertColor(mainColor.value);
});
const backgroundColor = toRef(() => rsvpStore.getColors.backgroundColor);

const FormChooserVersion = shallowRef(null);
const componentLoaded = ref(false);

function pickVersion() {
  if (formVersionList[rsvpStore.getCommonData.formVersion]) {
    FormChooserVersion.value = defineAsyncComponent(
      () =>
        import(
          `../components/form/versions/${
            formVersionList[rsvpStore.getCommonData.formVersion]
          }/FormChooser.vue`
        ),
    );
    componentLoaded.value = true;
  }
}

onMounted(() => {
  pickVersion();
});
</script>

<template>
  <div class="rsvp-version-c1">
    <LangChooser
      :main-color="mainColor"
      :background-color="backgroundColor"
      :store="rsvpStore"
    />
    <div
      class="rsvp-version-c1-background"
      :style="{
        backgroundImage: `url('${rsvpStore.getImages.rsvpBackground.url}')`,
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundColor: `${backgroundColor}`,
      }"
    ></div>
    <div class="rsvp-version-c1-border">
      <div class="rsvp-version-c1-container">
        <div class="rsvp-version-c1-container__subelement">
          <HeaderWidget
            :font="headerTextFont"
            :her-name="rsvpStore.getCommonData.herName"
            :his-name="rsvpStore.getCommonData.hisName"
            :header-separator="rsvpStore.getCommonData.headerSeparator"
            :main-color="stateStore.getColors.mainColor"
          />
          <img
            class="rsvp-version-c1-container-image"
            :src="rsvpStore.getImages.rsvp.url"
            :alt="rsvpStore.getImages.rsvp.name"
          />
          <ClockWidget
            v-if="rsvpStore.getCommonData.isClock"
            clock-text=" "
            :text-font="headerTextFont"
            class="rsvp-version-c1-clock"
          />
        </div>

        <div class="rsvp-version-c1-container__subelement">
          <div class="rsvp-version-c1-wedding-info">
            <img class="rsvp-version-c1-wedding-info__rings" :src="rings" />
            <div class="rsvp-version-c1-wedding-info-container">
              <h2 class="rsvp-version-c1-wedding-info-container__header">
                Ceremonia i Wesele
              </h2>
              <p class="rsvp-version-c1-wedding-info-container__date">
                {{
                  getStringDate(new Date(stateStore.getCommonData.eventDate))
                }}
              </p>
              <a
                v-if="places.common[0]"
                :href="places.common[0].link"
                class="rsvp-version-c1-wedding-info-container__church"
              >
                <img
                  class="rsvp-version-c1-wedding-info-container__church-image"
                  :src="church"
                />
                <p class="rsvp-version-c1-wedding-info-container__church-place">
                  {{ places.lang[0]?.name }}
                </p>
                <p class="rsvp-version-c1-wedding-info-container__church-place">
                  {{ places.lang[0]?.location }}
                </p>
              </a>
              <p class="rsvp-version-c1-wedding-info-container__church-time">
                godz. {{ rsvpStore.getCommonData.hour }}
              </p>
              <a
                v-if="places.common[1]"
                :href="places.common[1].link"
                class="rsvp-version-c1-wedding-info-container__party-pin"
              >
                <img
                  class="rsvp-version-c1-wedding-info-container__party-pin__image"
                  :src="pin"
                />
                <p
                  class="rsvp-version-c1-wedding-info-container__party-pin__text"
                >
                  {{ places.lang[1]?.name }}
                </p>
                <p
                  class="rsvp-version-c1-wedding-info-container__party-pin__text"
                >
                  {{ places.lang[1]?.location }}
                </p>
              </a>
            </div>
          </div>
        </div>

        <div class="rsvp-version-c1-container__subelement">
          <div class="rsvp-version-c1-wedding-rsvp__image">
            <p class="rsvp-version-c1-wedding-rsvp__image-text">
              Potwierdź swoją obecność
            </p>
            <img
              class="rsvp-version-c1-wedding-rsvp__image-img"
              :src="rsvpStore.getImages.rsvpForm.url"
            />
          </div>
          <FormChooserVersion v-if="componentLoaded" />
        </div>
      </div>
    </div>
  </div>
  <PageFooter />
</template>

<style lang="scss" scoped>
.rsvp-version-c1 {
  position: relative;
  display: flex;

  &-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    min-height: 100vh;
    z-index: 0;
  }

  &-border {
    background-attachment: fixed;
    position: relative;
    margin: 45px auto;
    max-width: 900px;
    width: 70%;
    padding: 25px;
    border: 2px solid v-bind(mainColor);
    border-radius: 36px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    @media screen and (max-width: $media-max-tablet) {
      width: 90%;
    }

    @media screen and (max-width: $media-max-phone) {
      border: none;
      padding: 0;
      margin: 25px auto;
    }
  }

  &-container {
    padding: 25px;
    padding-top: 35px;
    background-color: v-bind(backgroundColor);
    border-radius: 36px;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 125px;

    @media screen and (max-width: $media-max-phone) {
      padding-top: 25px;
      gap: 75px;
    }

    &-image {
      width: 80%;
      height: auto;
    }

    &__subelement {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 5px;

      @media screen and (max-width: $media-max-phone) {
        gap: 25px;
      }
    }
  }

  &-wedding {
    &-info {
      display: flex;
      flex-direction: column;
      gap: 15px;
      justify-content: center;
      align-items: center;
      width: 100%;

      &__rings {
        width: 90px;
        height: auto;

        @media screen and (max-width: $media-max-phone) {
          width: 70px;
        }
      }

      &-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 15px;
        color: v-bind(mainColor);

        @media screen and (max-width: $media-max-phone) {
          gap: 10px;
        }

        &__header {
          font-size: 100px;
          font-size: clamp(20px, 6vw, 100px);
          font-family: v-bind(headerTextFont);
          text-shadow: v-bind(inversedMainColor) 3px 3px;

          @media screen and (max-width: $media-max-phone) {
            font-size: 10vw;
          }
        }

        &__date {
          font-size: 85px;
          font-size: clamp(20px, 4.5vw, 85px);
          font-family: v-bind(headerTextFont);
          text-shadow: v-bind(inversedMainColor) 3px 3px;

          @media screen and (max-width: $media-max-phone) {
            font-size: 8vw;
          }
        }

        &__church {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 5px;
          cursor: pointer;

          &-image {
            width: 80px;
            height: auto;

            @media screen and (max-width: $media-max-phone) {
              width: 70px;
            }
          }

          &-place {
            text-align: center;
            font-size: 1.75vw;
            font-size: clamp(20px, 1.75vw, 40px);
            font-weight: 200;
            font-family: v-bind(textFont);

            @media screen and (max-width: $media-max-phone) {
              font-size: 4vw;
            }
          }

          &-time {
            font-size: 3.5vw;
            font-size: clamp(20px, 3.5vw, 85px);
            font-family: v-bind(headerTextFont);
            text-shadow: v-bind(inversedMainColor) 3px 3px;

            @media screen and (max-width: $media-max-phone) {
              font-size: 7vw;
            }
          }
        }

        &__party-pin {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 5px;
          cursor: pointer;

          &__image {
            width: 40px;
            height: auto;

            @media screen and (max-width: $media-max-phone) {
              width: 40px;
            }
          }

          &__text {
            text-align: center;
            font-size: 1.75vw;
            font-size: clamp(20px, 1.75vw, 40px);
            font-weight: 200;
            font-family: v-bind(textFont);

            @media screen and (max-width: $media-max-phone) {
              font-size: 4vw;
            }
          }
        }
      }
    }

    &-rsvp {
      &__image {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 0;
        margin: 0;

        &-text {
          color: v-bind(mainColor);
          text-align: center;
          font-size: 3vw;
          font-size: clamp(20px, 3vw, 60px);

          @media screen and (max-width: $media-max-phone) {
            font-size: 5.5vw;
          }
        }

        &-img {
          width: 40vw;
          height: auto;

          @media screen and (max-width: $media-max-phone) {
            width: 70vw;
          }
        }
      }
    }
  }
}
</style>
